// AI Research Lab Skill Data
// Skills mapped to salary impact, demand, and lab relevance

export const AI_RESEARCH_SKILL_SALARY_IMPACT = {
  // Foundational ML/AI
  "Linear Algebra & Matrix Theory": { impact: 20000, category: "foundational", demandScore: 85 },
  "Probability & Statistics": { impact: 18000, category: "foundational", demandScore: 90 },
  "Optimization Theory": { impact: 22000, category: "foundational", demandScore: 80 },
  "Information Theory": { impact: 20000, category: "foundational", demandScore: 70 },

  // Core ML
  "Deep Learning Fundamentals": { impact: 25000, category: "core", demandScore: 95 },
  "Neural Network Architectures": { impact: 28000, category: "core", demandScore: 90 },
  "Backpropagation & Gradient Descent": { impact: 20000, category: "core", demandScore: 85 },

  // Reinforcement Learning (Stanford CS234 aligned)
  "Markov Decision Processes (MDPs)": { impact: 30000, category: "rl", demandScore: 85 },
  "Policy Gradient Methods": { impact: 35000, category: "rl", demandScore: 88 },
  "Q-Learning & DQN": { impact: 28000, category: "rl", demandScore: 80 },
  "Actor-Critic Methods": { impact: 32000, category: "rl", demandScore: 82 },
  "Multi-Agent RL": { impact: 35000, category: "rl", demandScore: 75 },
  "Inverse Reinforcement Learning": { impact: 38000, category: "rl", demandScore: 70 },
  "Model-Based RL": { impact: 35000, category: "rl", demandScore: 78 },

  // Transformers & LLMs
  "Transformer Architecture": { impact: 40000, category: "llm", demandScore: 98 },
  "Attention Mechanisms": { impact: 35000, category: "llm", demandScore: 95 },
  "Pre-training & Fine-tuning": { impact: 38000, category: "llm", demandScore: 92 },
  "Prompt Engineering": { impact: 25000, category: "llm", demandScore: 88 },

  // RLHF & Alignment
  "RLHF (Reinforcement Learning from Human Feedback)": { impact: 50000, category: "alignment", demandScore: 95 },
  "Constitutional AI": { impact: 45000, category: "alignment", demandScore: 85 },
  "Reward Modeling": { impact: 45000, category: "alignment", demandScore: 88 },
  "AI Safety & Alignment": { impact: 55000, category: "alignment", demandScore: 92 },
  "Interpretability & Mechanistic Understanding": { impact: 50000, category: "alignment", demandScore: 85 },
  "Red-teaming & Adversarial Testing": { impact: 40000, category: "alignment", demandScore: 80 },

  // Infrastructure & Scale
  "Distributed Training (FSDP, DeepSpeed)": { impact: 45000, category: "infrastructure", demandScore: 90 },
  "GPU Programming (CUDA)": { impact: 40000, category: "infrastructure", demandScore: 85 },
  "JAX/Flax": { impact: 38000, category: "infrastructure", demandScore: 82 },
  "PyTorch Advanced": { impact: 35000, category: "infrastructure", demandScore: 95 },
  "Model Optimization (Quantization, Pruning)": { impact: 35000, category: "infrastructure", demandScore: 78 },

  // Research Skills
  "Academic Paper Writing": { impact: 25000, category: "research", demandScore: 75 },
  "Experiment Design & Ablations": { impact: 30000, category: "research", demandScore: 85 },
  "Benchmarking & Evaluation": { impact: 28000, category: "research", demandScore: 80 },
};

export const AI_RESEARCH_LABS = {
  openai: {
    name: "OpenAI",
    shortName: "OpenAI",
    careersUrl: "https://openai.com/careers",
    color: "#10a37f",
    roles: ["Research Scientist", "Research Engineer", "Applied AI Engineer"],
    salaryRange: { min: 350000, max: 650000 },
    focusAreas: ["GPT/LLMs", "RLHF", "Scaling", "Safety"],
    topSkills: [
      "RLHF (Reinforcement Learning from Human Feedback)",
      "Transformer Architecture",
      "Distributed Training (FSDP, DeepSpeed)",
      "AI Safety & Alignment",
      "Pre-training & Fine-tuning"
    ]
  },
  anthropic: {
    name: "Anthropic",
    shortName: "Anthropic",
    careersUrl: "https://www.anthropic.com/careers",
    color: "#d4a574",
    roles: ["Research Scientist", "Research Engineer", "Policy Researcher"],
    salaryRange: { min: 350000, max: 600000 },
    focusAreas: ["Constitutional AI", "Interpretability", "Safety", "RLHF"],
    topSkills: [
      "Constitutional AI",
      "RLHF (Reinforcement Learning from Human Feedback)",
      "Interpretability & Mechanistic Understanding",
      "Reward Modeling",
      "AI Safety & Alignment"
    ]
  },
  deepmind: {
    name: "Google DeepMind",
    shortName: "DeepMind",
    careersUrl: "https://deepmind.google/careers",
    color: "#4285f4",
    roles: ["Research Scientist", "Research Engineer", "Staff SWE"],
    salaryRange: { min: 300000, max: 550000 },
    focusAreas: ["RL", "Science AI", "Gemini", "Neuroscience"],
    topSkills: [
      "Multi-Agent RL",
      "Model-Based RL",
      "JAX/Flax",
      "Distributed Training (FSDP, DeepSpeed)",
      "Policy Gradient Methods"
    ]
  },
  metaFair: {
    name: "Meta FAIR",
    shortName: "FAIR",
    careersUrl: "https://ai.meta.com/join-us",
    color: "#0866ff",
    roles: ["Research Scientist", "Research Engineer", "AI Engineer"],
    salaryRange: { min: 280000, max: 500000 },
    focusAreas: ["LLaMA", "Vision", "Open Research", "Embodied AI"],
    topSkills: [
      "PyTorch Advanced",
      "Transformer Architecture",
      "Pre-training & Fine-tuning",
      "Distributed Training (FSDP, DeepSpeed)",
      "Deep Learning Fundamentals"
    ]
  }
};

// Skill categories with display info
export const SKILL_CATEGORIES = {
  foundational: { label: "Foundational Math", color: "#6b7280" },
  core: { label: "Core ML", color: "#3b82f6" },
  rl: { label: "Reinforcement Learning", color: "#8b5cf6" },
  llm: { label: "LLMs & Transformers", color: "#f59e0b" },
  alignment: { label: "Safety & Alignment", color: "#ef4444" },
  infrastructure: { label: "Infrastructure", color: "#10b981" },
  research: { label: "Research Skills", color: "#6366f1" }
};

// Get skills sorted by impact for a specific lab
export const getLabPrioritizedSkills = (labKey) => {
  const lab = AI_RESEARCH_LABS[labKey];
  if (!lab) return [];

  return Object.entries(AI_RESEARCH_SKILL_SALARY_IMPACT)
    .map(([skill, data]) => ({
      skill,
      ...data,
      labRelevance: lab.topSkills.includes(skill) ? 100 : 50
    }))
    .sort((a, b) => {
      // Prioritize by lab relevance first, then by impact
      const aScore = a.labRelevance * 0.4 + (a.impact / 550) * 0.6;
      const bScore = b.labRelevance * 0.4 + (b.impact / 550) * 0.6;
      return bScore - aScore;
    });
};

// Get skills the user is missing, prioritized by impact and lab relevance
export const getMissingSkillsForLabs = (userSkills, targetLabs) => {
  const userSkillSet = new Set(userSkills || []);
  const labObjects = targetLabs.map(key => AI_RESEARCH_LABS[key]).filter(Boolean);

  return Object.entries(AI_RESEARCH_SKILL_SALARY_IMPACT)
    .filter(([skill]) => !userSkillSet.has(skill))
    .map(([skill, data]) => {
      // Calculate lab relevance across all target labs
      const labRelevance = labObjects.reduce((sum, lab) =>
        sum + (lab.topSkills.includes(skill) ? 25 : 0), 0
      );
      return {
        skill,
        ...data,
        labRelevance,
        priority: data.impact * (labRelevance / 100 + 1)
      };
    })
    .sort((a, b) => b.priority - a.priority);
};
